import { AxiosResponse } from 'axios'
import fetch from './fetch'
import customerBffFetch from './customer-bff-fetch'
import type {
  CreatePaymentResponse,
  CreatePaymentResponseYape,
  GetPaymentStatusResponse,
  RedirectToPaymentGatewayParams,
} from './utils'
import { formatPaymentMethods, payInBolivia, payInChile } from './utils'
import { buildPaymentMethodsEndpoint } from './countryConfig'
import { ApplyCouponParams, ApplyGiftCardParams, ByHash, Country, PaymentMethodsObject, ProductCart } from '../../types'
import { getCartId, getStoredDistributionCenter, getStoredMarket } from '../../utils/store'
import { CLLineItem, CLOrder, ResourceType } from '../CommerceLayer/types'
import { ImportStatus } from './types'
import { log } from '../../utils/log'
import { timeout } from '../../utils/promises'
import { getOrderForCheckout } from './orders'
import { Market } from '../Location/cities'
import { DiscountDetails } from '../../context/ShoppingCart/context'

const getPaymentMethods = async (country: Country): Promise<PaymentMethodsObject> => {
  try {
    const endpoint = buildPaymentMethodsEndpoint(country)

    const { data } = await fetch(endpoint, { method: 'GET' })

    return formatPaymentMethods(data, country)
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Error desconocido al obtener métodos de pago'
    throw new Error(`Failed to fetch payment methods for ${country}: ${errorMessage}`)
  }
}

/**
 * Init Chile payment
 */
const initPaymentChile = async (orderId: string, returnUrl: string, cancelUrl: string) => {
  const { data } = await fetch('/initPaymentChile', {
    data: { orderId, returnUrl, cancelUrl },
    method: 'POST',
  })
  return data
}

const getReturnAndCancelUrl = (orderId: string, isBolivia = false) => {
  const domain = typeof window !== 'undefined' ? window.location.origin : ''
  return [
    `${domain}/payment/${isBolivia ? `pending?req_reference_number=${orderId}` : `processing?orderId=${orderId}`}`,
    `${domain}/payment/cancelled?req_reference_number=${orderId}`,
  ]
}

const getChilePaymentUrl = async (orderId: string): Promise<string> => {
  const [returnUrl, cancelUrl] = getReturnAndCancelUrl(orderId)
  const { paymentUrl: url } = await initPaymentChile(orderId, returnUrl, cancelUrl)
  return url
}

/**
 * Init Bolivia payment
 */
export const initPaymentBolivia = async (orderId: string, sessionId: string, country: Country) => {
  const [returnUrl, cancelUrl] = getReturnAndCancelUrl(orderId, true)
  const { data } = await customerBffFetch('/payments/init', {
    data: { orderId, sessionId, returnUrl, cancelUrl, country },
    method: 'POST',
  })
  return data
}

const redirectToPaymentGateway = async (params: RedirectToPaymentGatewayParams) => {
  if (params.countryCode === Country.CHILE) {
    await payInChile(params)
  } else if (params.countryCode === Country.BOLIVIA) {
    await payInBolivia(params)
  }
}

const applyCoupon = async ({
  code,
  orderId,
  market,
  country,
  currentMarket,
  distributionCenterSlug,
}: ApplyCouponParams) => {
  const couponCode = code.toUpperCase()
  const {
    data: { discountedAmount, isRaw, discountDetails },
  }: AxiosResponse<{
    discountedAmount: number
    isRaw?: boolean
    discountDetails: DiscountDetails[]
  }> = await customerBffFetch(`/coupons/apply`, {
    method: 'POST',
    data: { coupon: couponCode, orderId, country, market, currentMarket: currentMarket.slug, distributionCenterSlug },
  })

  return { discountedAmount, isRaw, discountDetails }
}

interface RemoveCouponParams {
  orderId: string
  country: Country
  currentMarket: Market
  distributionCenterSlug: string
}

const removeCoupon = async ({ orderId, country, currentMarket, distributionCenterSlug }: RemoveCouponParams) => {
  const {
    data: { discountAmount },
  }: AxiosResponse<{
    discountAmount: number
  }> = await customerBffFetch(`/coupons/revert`, {
    method: 'POST',
    data: { orderId, country, currentMarket: currentMarket.slug, distributionCenterSlug },
  })
}

const applyGiftCard = async ({ giftCard, orderId, marketNumber, country }: ApplyGiftCardParams) => {
  const {
    data: { discountAmount, isRaw, discountDetails },
  }: AxiosResponse<{
    discountAmount: number
    isRaw?: boolean
    discountDetails: DiscountDetails
  }> = await customerBffFetch(`/gift-card/apply`, {
    method: 'POST',
    data: { giftCard, orderId, country, marketNumber },
  })

  return { discountedAmount: discountAmount, isRaw, discountDetails }
}

const removeGiftcard = async ({ orderId, country, currentMarket }: RemoveCouponParams) =>
  customerBffFetch(`/gift-card/revert`, {
    method: 'POST',
    data: { orderId, country, currentMarket },
  })

type RetryPaymentResponse = AxiosResponse<{
  order: CLOrder
  skus: CLLineItem[]
  hasStockError?: boolean
  outOfStock?: string[]
}>

const retryPaymentOrRepeatOrder = async (orderId: string, country: Country, slugLocation: string) => {
  const { data }: RetryPaymentResponse = await fetch('/orders/repeat', {
    method: 'POST',
    data: { orderId, country, slugLocation },
  })

  return data
}

type SendEmailResponse = {
  data: string
}

interface SendEmailParams {
  message: string
  subject: string
  country: Country
}

const sendCustomEmail = async (params: SendEmailParams) => {
  const { data }: SendEmailResponse = await fetch('/sendCustomEmail', {
    method: 'POST',
    data: params,
  })

  return data
}

type ResponseStockItem = {
  sku: string
  quantity: number
}

export const checkStock = async (products: ByHash, country: Country, currentCity: Market) => {
  const city = getStoredMarket() ?? currentCity
  const distributionCenter = getStoredDistributionCenter()

  if (distributionCenter && distributionCenter.commerceLayer.stockLocation?.number) {
    const {
      commerceLayer: {
        stockLocation: { number: stockLocationNumber },
      },
    } = distributionCenter
    const {
      data: { unavailable },
    }: AxiosResponse<{ unavailable: ResponseStockItem[] }> = await fetch(`/stock_items/check`, {
      data: { skus: Object.keys(products), country, stockLocationNumber },
      method: 'POST',
    })

    return unavailable
  }
}

export const getSkuStock = async (sku: string, country: Country, stockLocationNumber: number) => {
  const { data }: AxiosResponse<ResponseStockItem> = await fetch(
    `/stock_items/stock?sku=${sku}&country=${country}&stockLocationNumber=${stockLocationNumber}`,
  )

  return data
}

export const getGiftCardBalance = async (giftCardCode: string, market: number, country: Country) => {
  const {
    data: { balance_cents },
  }: AxiosResponse<{ balance_cents: number }> = await fetch(`/gift_card/balance`, {
    data: { market, country, giftCardCode },
    method: 'POST',
  })

  return balance_cents
}

export type CreatePaymentParams = {
  orderId: string
  country: Country
  paymentOptionId: number
  paymentOptionName?: string
  paymentOptionData?: Record<string, unknown>
}

export const createPayment = async ({
  orderId,
  country,
  paymentOptionId,
  paymentOptionData,
  paymentOptionName,
}: CreatePaymentParams) => {
  const { data: response }: AxiosResponse<CreatePaymentResponse | CreatePaymentResponseYape> = await customerBffFetch(
    `/payments/create`,
    {
      method: 'POST',
      data: {
        orderId,
        country,
        paymentOptionId,
        ...(paymentOptionName && { paymentOptionName }),
        ...(paymentOptionData && { paymentOptionData }),
      },
    },
  )

  return response
}

export const getPaymentStatus = async ({ orderId }: Pick<CreatePaymentParams, 'orderId'>) => {
  const { data: response }: AxiosResponse<GetPaymentStatusResponse> = await customerBffFetch(
    `orders/${orderId}/payment-status`,
    {
      method: 'GET',
    },
  )

  return response
}

export const getImportStatus = async (id: string, country: Country) => {
  const { data }: AxiosResponse<{ id: string; resourceType: ResourceType; status: ImportStatus }> = await fetch(
    `/${country}/imports/${id}`,
  )

  return data
}

export const checkImportStatus = async (importId: string, country: Country) => {
  let counter = 0

  const { status: importStatus, ...importData } = await getImportStatus(importId, country)
  if (importStatus === ImportStatus.COMPLETED) {
    log.trace('COMPLETED', { importStatus, ...importData })

    return Promise.resolve()
  }

  counter += 1
  log.trace('🔥', counter, importStatus)

  await timeout(400)
  await checkImportStatus(importId, country)
}

type PrepareOrderForCheckoutParams = {
  country: Country
  orderId?: string | null | undefined
  customerEmail: string
  lineItems: ProductCart[]
  orderMetadata?: Partial<CLOrder['attributes']['metadata']>
}

export type PrepareOrderForCheckoutResponse = {
  order: CLOrder
  hasStockError?: boolean
  outOfStock?: string[]
  skus: CLLineItem[]
}

export const prepareOrderForCheckout = async (
  params: PrepareOrderForCheckoutParams,
): Promise<PrepareOrderForCheckoutResponse> => {
  const { orderMetadata = {}, country, ...restParams } = params
  const cartId = getCartId()
  const currentMarket = getStoredMarket()
  const currentDistributionCenter = getStoredDistributionCenter()

  if (!currentMarket || !currentDistributionCenter?.commerceLayer?.market) throw new Error('Market is not defined')

  const {
    data: response,
  }: AxiosResponse<PrepareOrderForCheckoutResponse & { importId?: string; orderId?: string }> = await customerBffFetch(
    `/orders/checkout`,
    {
      method: 'POST',
      data: {
        ...restParams,
        country,
        metadata: {
          ...orderMetadata,
          cartId,
        },
        marketId: currentDistributionCenter.commerceLayer.market.id,
      },
    },
  )

  if (!response.orderId) return response

  return getOrderForCheckout(response.orderId, country)
}

type updateOrderParams = {
  orderId: string
  country: string
  attributes: { customer_email: string; metadata: Record<string, string>; guest?: boolean }
}

export type updateOrderResponse = {
  statusCode: number
  body: string
}

export const updateOrder = async (params: updateOrderParams): Promise<updateOrderResponse> => {
  const { data: response }: AxiosResponse<updateOrderResponse> = await fetch(`/orders/${params.orderId}`, {
    method: 'PATCH',
    data: params,
  })

  return response
}

export interface MercadoPagoCheckoutBrickData {
  payment_method: {
    id: string
    type: string
    token: string
    installments: number
  }
  payer: {
    email: string
    identification: {
      number: string
      type: string
    }
  }
}

export {
  getPaymentMethods,
  getChilePaymentUrl,
  redirectToPaymentGateway,
  applyCoupon,
  applyGiftCard,
  retryPaymentOrRepeatOrder,
  removeCoupon,
  removeGiftcard,
  sendCustomEmail,
}

export type quoteCitiesResponse = {
  statusCode: number
  cities: {
    name: string
    id: number
  }[]
}

export const getEventQuoteCities = async (country: string) => {
  const { data: response }: AxiosResponse<quoteCitiesResponse> = await fetch(
    `/${country}/customers/event-quote-cities`,
    {
      method: 'GET',
    },
  )

  return response
}

export type quoteProps = {
  eventType?: string
  date?: string
  inviteNumber?: number
  name: string
  cityId: number
  rut: string
  email: string
  message: string
  metadata?: any
}

export const createQuote = async (country: string, data: quoteProps) => {
  const { data: response }: AxiosResponse<quoteCitiesResponse> = await fetch(`/${country}/customers/create-quote`, {
    method: 'POST',
    data,
  })

  return response
}

export const verifyRecaptcha = async (country: string, token: string) => {
  const { data } = await customerBffFetch(`/gift-card/verify-recaptcha`, {
    method: 'POST',
    data: {
      country,
      token,
    },
  })

  return data.isValid
}
